import { ref, type ComputedRef } from 'vue'
import { useApiClient } from '~/services/api'
import type { BrandStoreSearchStore } from '~/services/api/interfaces/Brand'
import { GeocodingPlaceType, GeocodingPlaceTypeValues } from '~/enums'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLocalizedMessage } from '~/composables/useLocalizedMessage'
import { Normalization } from '~/utils/normalization'

interface BrandStoreSearchSelection {
  address: string
  lat?: number
  lng?: number
  bbox?: number[] | string | null
  place_type?: string | null
}

// 'poi' is intentionally excluded — brand store search doesn't support point-of-interest lookups.
const BRAND_ALLOWED_PLACE_TYPES = GeocodingPlaceTypeValues.filter(
  (value) => value !== GeocodingPlaceType.POI
)

export function useBrandStoreAddressSearch(slug: ComputedRef<string>) {
  const { brand: brandApi } = useApiClient()
  const t = usePresentationTranslator()

  const query = ref('')
  const selection = ref<BrandStoreSearchSelection | null>(null)
  const isSearching = ref(false)
  const isRedirecting = ref(false)
  const hasSearched = ref(false)
  const error = useLocalizedMessage()
  const noResultsMessage = useLocalizedMessage()
  const searchResults = ref<BrandStoreSearchStore[]>([])
  const submittedAddress = ref('')

  let requestId = 0

  const normalizeBbox = (bbox: BrandStoreSearchSelection['bbox']) => {
    if (Array.isArray(bbox) && bbox.length === 4) return bbox.join(',')
    if (typeof bbox === 'string' && bbox.trim()) return bbox

    return null
  }

  const normalizePlaceType = (placeType: BrandStoreSearchSelection['place_type']) =>
    Normalization.enumValue(placeType, BRAND_ALLOWED_PLACE_TYPES, null)

  const updateQuery = (nextQuery: string) => {
    query.value = nextQuery

    if (nextQuery.trim() !== submittedAddress.value.trim()) {
      error.clear()
      noResultsMessage.clear()
    }

    if (!selection.value) return

    if (nextQuery.trim() !== selection.value.address.trim()) {
      selection.value = null
    }
  }

  const applySelection = (nextSelection: BrandStoreSearchSelection) => {
    selection.value = {
      address: nextSelection.address,
      ...(nextSelection.lat === undefined ? {} : { lat: nextSelection.lat }),
      ...(nextSelection.lng === undefined ? {} : { lng: nextSelection.lng }),
      bbox: nextSelection.bbox ?? null,
      place_type: nextSelection.place_type ?? null
    }
    query.value = nextSelection.address
  }

  const submit = async () => {
    const trimmedQuery = query.value.trim()

    if (!slug.value || isSearching.value || !trimmedQuery) return

    const currentRequestId = ++requestId
    isSearching.value = true
    error.clear()
    noResultsMessage.clear()
    submittedAddress.value = trimmedQuery

    const activeSelection = selection.value
    const hasStructuredSelection =
      activeSelection !== null && activeSelection.address.trim() === trimmedQuery
    const normalizedBbox = normalizeBbox(activeSelection?.bbox)
    const normalizedPlaceType = normalizePlaceType(activeSelection?.place_type)

    const params =
      hasStructuredSelection && normalizedBbox !== null && normalizedPlaceType !== null
        ? {
            q: trimmedQuery,
            ...(activeSelection.lat != null ? { lat: activeSelection.lat } : {}),
            ...(activeSelection.lng != null ? { lng: activeSelection.lng } : {}),
            bbox: normalizedBbox,
            place_type: normalizedPlaceType
          }
        : { q: trimmedQuery }

    try {
      const response = await brandApi().publicStoreSearch(slug.value, params)

      if (currentRequestId !== requestId) return

      const data = response.data
      searchResults.value = data.results
      hasSearched.value = true

      if (data.count === 0) {
        noResultsMessage.set(() => t('common.brandStoreAddressSearch.noStoresFoundMessage'))
      }

      const firstResult = data.results[0]

      if (firstResult) {
        isRedirecting.value = true
        await navigateTo(`/store/${firstResult.slug}`)
        return
      }
    } catch (e: unknown) {
      if (currentRequestId !== requestId) return
      const problem = AppProblemModel.normalize(e, { context: 'brand store address search' })
      error.set(() =>
        resolveProblemMessage(problem, t, t('common.brandStoreAddressSearch.searchFailedMessage'))
      )
      searchResults.value = []
      hasSearched.value = true
    } finally {
      if (currentRequestId === requestId) {
        isSearching.value = false
      }
    }
  }

  const clear = () => {
    requestId++
    query.value = ''
    selection.value = null
    submittedAddress.value = ''
    hasSearched.value = false
    searchResults.value = []
    error.clear()
    noResultsMessage.clear()
    isSearching.value = false
    isRedirecting.value = false
  }

  return {
    query,
    selection,
    isSearching,
    isRedirecting,
    hasSearched,
    error: error.message,
    noResultsMessage: noResultsMessage.message,
    searchResults,
    submittedAddress,
    applySelection,
    updateQuery,
    submit,
    clear
  }
}
